import { useState, useCallback } from 'react';

import get from 'lodash/get';

import { IOktaError, TLoginState, TOktaApiResponse } from '../interfaces';

import api from './api';

export type OktaLoginViewProps = {
  deviceToken: string;
  handleSuccess: (data: Partial<TOktaApiResponse>) => void;
  initialView?: TLoginState;
};

export function useOktaLoginView({
  deviceToken,
  handleSuccess,
  initialView = 'LOGIN',
}: OktaLoginViewProps) {
  const [stateToken, setStateToken] = useState<string | null>(null);
  const [oktaApiUrls, setOktaApiUrls] = useState(() => {
    return {
      verify: '',
      enroll: '',
      activate: '',
      resend: '',
      resetPassword: '',
    };
  });
  const [currentLoginState, setCurrentLoginState] =
    useState<TLoginState>(initialView);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorView, setErrorView] = useState<TLoginState | null>(null);

  function onSuccess({
    status,
    stateToken,
    _embedded,
    _links,
    sessionToken,
  }: TOktaApiResponse) {
    switch (status) {
      case 'MFA_REQUIRED':
        setCurrentLoginState('MFA_SEND_CODE');
        setStateToken(stateToken);
        setOktaApiUrls((currentOktaApiUrls) => {
          const verify = get(_embedded, 'factors[0]._links.verify.href');
          return {
            ...currentOktaApiUrls,
            verify,
          };
        });
        setPhoneNumber(get(_embedded, 'factors[0].profile.phoneNumber'));
        setIsSubmitting(false);
        break;
      case 'MFA_ENROLL':
        setCurrentLoginState('MFA_ENROLL');
        setStateToken(stateToken);
        setOktaApiUrls((currentOktaApiUrls) => {
          const enroll = get(_embedded, 'factors[0]._links.enroll.href');
          return {
            ...currentOktaApiUrls,
            enroll,
          };
        });
        setIsSubmitting(false);
        break;
      case 'PASSWORD_RESET':
        setCurrentLoginState('PASSWORD_RESET');
        setStateToken(stateToken);
        setOktaApiUrls((currentOktaApiUrls) => {
          const resetPassword = get(_links, 'next.href');
          return {
            ...currentOktaApiUrls,
            resetPassword,
          };
        });
        setIsSubmitting(false);
        break;
      case 'SUCCESS':
        handleSuccess({
          status,
          stateToken,
          sessionToken,
        });
        break;
      default:
        setIsSubmitting(false);
    }
  }

  function authenticate(username: string, password: string) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaAuthenticate({
          username,
          password,
          options: {
            multiOptionalFactorEnroll: true,
          },
          context: {
            deviceToken,
          },
        })
        .then((data) => {
          onSuccess(data);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          setErrorView(currentLoginState);
          reject(error);
        });
    });
  }

  function sendCode() {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest({
          url: oktaApiUrls.verify,
          opts: {
            passCode: '',
            stateToken,
            context: {
              deviceToken,
            },
          },
        })
        .then(
          ({
            _links: {
              resend: [{ href }],
            },
          }) => {
            setIsSubmitting(false);
            setCurrentLoginState('MFA_VERIFY_CODE');
            setOktaApiUrls((currentOktaApiUrls) => {
              return {
                ...currentOktaApiUrls,
                resend: href,
              };
            });
            resolve({});
          }
        )
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          reject(error);
        });
    });
  }

  function verifyCode(
    passCode: string,
    opts?: { shouldRememberDevice: boolean }
  ) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest({
          url: `${oktaApiUrls.verify}${
            opts?.shouldRememberDevice ? '?rememberDevice=true' : ''
          }`,
          opts: {
            passCode,
            stateToken,
            context: {
              deviceToken,
            },
          },
        })
        .then((data) => {
          onSuccess(data);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setErrorView(currentLoginState);
          setIsSubmitting(false);
          reject(error);
        });
    });
  }

  function resendCode() {
    if (errorView) {
      setErrorView(null);
    }
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest({
          url: oktaApiUrls.resend,
          opts: {
            stateToken,
            context: {
              deviceToken,
            },
          },
        })
        .then((data) => {
          setIsSubmitting(false);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          reject(error);
        });
    });
  }

  function resetPassword(username: string) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaResetPassword({
          username,
          factorType: 'EMAIL',
          context: {
            deviceToken,
          },
        })
        .then((data) => {
          setIsSubmitting(false);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          reject(error);
        });
    });
  }

  const validateRecoveryToken = useCallback((recoveryToken: string) => {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaValidateRecoveryToken({
          recoveryToken,
          context: {
            deviceToken,
          },
        })
        .then((data) => {
          onSuccess(data);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          setErrorView('PASSWORD_RESET');
          reject(error);
        });
    });
  }, []);

  function updatePassword(newPassword: string) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest({
          url: oktaApiUrls.resetPassword,
          opts: {
            stateToken,
            newPassword,
            context: {
              deviceToken,
            },
          },
        })
        .then((data) => {
          onSuccess(data);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          setErrorView(currentLoginState);
          reject(error);
        });
    });
  }

  function enrollMFA(phoneNumber: string) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest({
          url: `${oktaApiUrls.enroll}?updatePhone=true`,
          opts: {
            stateToken,
            factorType: 'sms',
            provider: 'OKTA',
            profile: {
              phoneNumber: phoneNumber.replace('+', ''),
            },
            context: {
              deviceToken,
            },
          },
        })
        .then(({ _links }) => {
          setIsSubmitting(false);
          setCurrentLoginState('MFA_ACTIVATE');
          setOktaApiUrls((currentOktaApiUrls) => {
            const activate = get(_links, 'next.href');
            const resend = get(_links, 'resend[0].href');
            return {
              ...currentOktaApiUrls,
              activate,
              resend,
            };
          });
          setPhoneNumber(phoneNumber);
          resolve({});
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          setErrorView(currentLoginState);
          reject(error);
        });
    });
  }

  function activateMFA(passCode: string) {
    setIsSubmitting(true);
    return new Promise((resolve, reject) => {
      api
        .oktaPostRequest({
          url: oktaApiUrls.activate,
          opts: {
            stateToken,
            passCode,
            context: {
              deviceToken,
            },
          },
        })
        .then((data) => {
          onSuccess(data);
          resolve(data);
        })
        .catch((error: IOktaError) => {
          setIsSubmitting(false);
          setErrorView(currentLoginState);
          reject(error);
        });
    });
  }

  function toggleForgotPassword() {
    if (currentLoginState === 'LOGIN') {
      setCurrentLoginState('FORGOT_PASSWORD');
    } else {
      setCurrentLoginState('LOGIN');
    }
  }

  const getOktaStateToken = useCallback(
    (
      handleRedirect: () => void,
      oktaLoginInfo?: {
        username: string;
        password: string;
      }
    ) => {
      if (oktaLoginInfo) {
        api
          .oktaAuthenticate({
            username: oktaLoginInfo.username,
            password: oktaLoginInfo.password,
            options: {
              multiOptionalFactorEnroll: true,
            },
            context: {
              deviceToken,
            },
          })
          .then(({ status, stateToken, _embedded }) => {
            if (status === 'MFA_ENROLL') {
              setStateToken(stateToken);
              setOktaApiUrls((currentOktaApiUrls) => {
                const enroll = get(_embedded, 'factors[0]._links.enroll.href');
                return {
                  ...currentOktaApiUrls,
                  enroll,
                };
              });
            } else {
              handleRedirect();
            }
          });
      } else {
        handleRedirect();
      }
    },
    []
  );

  return {
    currentLoginState,
    phoneNumber,
    isSubmitting,
    errorView,
    authenticate,
    sendCode,
    verifyCode,
    resendCode,
    resetPassword,
    enrollMFA,
    activateMFA,
    toggleForgotPassword,
    getOktaStateToken,
    validateRecoveryToken,
    updatePassword,
  };
}
